import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { Cover } from '../../../music24/components/primitives/Cover';
import { CatalogReleaseEvent } from '../../../../redux/api/fymAPI.types';
import { formatCatalogDay } from '../catalogDates';
import { releaseMessages } from './releaseMessages';
import { Panel } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the release cards; each part uses its own slice of it */
import styles from './ReleaseCards.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReleaseAskCardProps {
  event: CatalogReleaseEvent;
  disabled: boolean;
  onLink: (event: CatalogReleaseEvent) => void;
  onDismiss: (event: CatalogReleaseEvent) => void;
}

/**
 * Design g3, the amber card: a released track looks like the unreleased row, but the match is
 * not sure (D14). "Yes, link it" links them; "No" closes the card for good.
 */
const ReleaseAskCardComponent: React.FC<ReleaseAskCardProps> = ({ event, disabled, onLink, onDismiss }) => {
  const intl = useIntl();
  const link = useCallback(() => onLink(event), [event, onLink]);
  const dismiss = useCallback(() => onDismiss(event), [event, onDismiss]);
  const { candidate } = event;

  return (
    <div className={clsx(styles.card, styles.ask)} role="status">
      <div className={styles.grow}>
        <span className={styles.title}>{intl.formatMessage(releaseMessages.askTitle, { title: event.title })}</span>
        <span className={styles.body}>{intl.formatMessage(releaseMessages.askBody)}</span>
        <div className={styles.vs}>
          <Panel nested className={styles.side}>
            <Cover label={event.title} size={34} />
            <div className={styles.grow}>
              <span className={styles.sideName}>{event.title}</span>
              <span className={styles.sideSub}>{intl.formatMessage(releaseMessages.yourRow)}</span>
            </div>
          </Panel>
          <span className={styles.mid}>=?</span>
          <Panel nested className={styles.side}>
            <Cover src={candidate.artworkUrl ?? undefined} label={candidate.title} size={34} />
            <div className={styles.grow}>
              <span className={styles.sideName}>{candidate.title}</span>
              <span className={styles.sideSub}>
                {candidate.releaseDate === null
                  ? candidate.artistName
                  : intl.formatMessage(releaseMessages.outOn, { date: formatCatalogDay(intl, candidate.releaseDate) })}
              </span>
            </div>
          </Panel>
        </div>
      </div>
      <div className="d-flex flex-column gap-2">
        <SMLButton size="sm" color="primary" omitCap disabled={disabled} onClick={link}>
          {intl.formatMessage(releaseMessages.yes)}
        </SMLButton>
        <SMLButton size="sm" outline omitCap disabled={disabled} onClick={dismiss}>
          {intl.formatMessage(releaseMessages.no)}
        </SMLButton>
      </div>
    </div>
  );
};

export const ReleaseAskCard = TypedMemo(ReleaseAskCardComponent);
